import { useEffect } from 'react';
import { appPath } from '@featit/shared/addressing/paths';
import { AGENT_START_MAC_SECTION_ANCHOR } from '@featit/shared/agent-start-mac-place';
import { isDesktopRuntime } from './desktopDetection';
import type { INavigationTarget } from './useDesktopReturnLinks';

/**
 * F-0572: the menu-bar menu's routes, by the names the desktop sends (`background_mode.rs`
 * `RELAY_STATUS_ROUTE`). The path is written here, once; the desktop never names one.
 *
 * ⛔ Only these names route. Anything else the desktop answers with goes nowhere.
 */
export const DESKTOP_MENU_ROUTES: Readonly<Record<string, string>> = {
  // *Relay status…*: this machine's relay, where its status and switches are: its bridge node in the
  // Agents explorer, by Infrastructure's relay address, which redirects there (F-0601 AMX-6b, AMX-62).
  'relay-status': `${appPath('/infrastructure')}?section=relay`,
  // I-1786: *Starting agent sessions…*: this machine's node, Settings tab, at the section that grants
  // what a start may use (the redirect carries the anchor, AMX-6c).
  'start-settings': `${appPath('/infrastructure')}#${AGENT_START_MAC_SECTION_ANCHOR}`,
  // F-0654 FP-24: *Pause my agents…*: the Agents view with the Pause sheet open, set to my agents.
  'pause-my-agents': `${appPath('/agents')}?pause=mine`,
};

/** The event the desktop sends when the menu asks a window for a route (no payload). */
export const DESKTOP_MENU_ROUTE_EVENT = 'featit://menu-route';

/** The path for a route name the desktop answered with, or null for anything else. */
export function desktopMenuRoutePath(name: unknown): string | null {
  if (typeof name !== 'string') return null;
  return Object.prototype.hasOwnProperty.call(DESKTOP_MENU_ROUTES, name)
    ? DESKTOP_MENU_ROUTES[name]
    : null;
}

/**
 * Listens for the menu's route requests and takes one already waiting (the window was built for
 * it). The name is taken from the desktop, once, by `take_desktop_menu_route`, as return links are.
 */
export async function listenForDesktopMenuRoutes(
  onPath: (path: string) => void,
): Promise<() => void> {
  if (!isDesktopRuntime()) return () => {};
  const [{ listen }, { invoke }] = await Promise.all([
    import('@tauri-apps/api/event'),
    import('@tauri-apps/api/core'),
  ]);
  const take = async () => {
    try {
      const path = desktopMenuRoutePath(await invoke('take_desktop_menu_route'));
      if (path) onPath(path);
    } catch {
      // An app from before F-0572 slice 2 has no such command: nothing to route.
    }
  };
  let unlisten: () => void = () => {};
  try {
    unlisten = await listen(DESKTOP_MENU_ROUTE_EVENT, () => void take());
  } catch {
    // No event bridge (a fixture): the take below still routes a waiting request.
  }
  await take();
  return unlisten;
}

/** Routes the menu-bar menu's *Relay status…* in this window. */
export function useDesktopMenuRoutes(router: INavigationTarget): void {
  useEffect(() => {
    let stop: () => void = () => {};
    let disposed = false;
    void listenForDesktopMenuRoutes((path) => {
      void router.navigate(path, { state: null });
    }).then((unlisten) => {
      if (disposed) unlisten();
      else stop = unlisten;
    });
    return () => {
      disposed = true;
      stop();
    };
  }, [router]);
}
